// [syn] screenshot-grade seed — Module 9 maintenance strategy (paul-tarp.A20 #422).
// v19 M9 §4's condition-monitoring inventory (BM01 trunnion bearing, BM01
// liner bolts, BM01 motor stator, Cyclone Feed Pump, Zn Rougher Agitator
// gearbox, Belt Scale WIT-021) is real — the Equipment Health tab's
// `CONDITION_MONITORING_POINTS` (`Scene.transform.ts`) reuses it verbatim.
// Everything on this page (per-item FMEA task type, criticality tier,
// shutdown-window links) is [syn]: M9 §3 documents the task-type taxonomy
// and its full-dataset counts, not a per-item mapping for these ten
// maintainable items, and M9 §6 is explicit that no shutdown calendar
// exists in the document set.

import type { DowntimeItemId } from './downtime.seed';

/** v19 M9 §3 — real occurrence counts across the full 60+ row FMEA. */
export type FmeaTaskType =
  | 'PM'
  | 'CBM + PM'
  | 'PM + CBM'
  | 'Failure-finding + PM'
  | 'CBM'
  | 'CBM + Operational'
  | 'Operational + PM'
  | 'Operational'
  | 'Redundancy + Failure-finding';

export const FMEA_TASK_TYPE_COUNTS: readonly { taskType: FmeaTaskType; occurrences: number }[] = [
  { taskType: 'PM', occurrences: 9 },
  { taskType: 'CBM + PM', occurrences: 9 },
  { taskType: 'PM + CBM', occurrences: 8 },
  { taskType: 'Failure-finding + PM', occurrences: 6 },
  { taskType: 'CBM', occurrences: 5 },
  { taskType: 'CBM + Operational', occurrences: 7 },
  { taskType: 'Operational + PM', occurrences: 5 },
  { taskType: 'Operational', occurrences: 3 },
  { taskType: 'Redundancy + Failure-finding', occurrences: 3 },
];

export type CriticalityTier = 'Critical' | 'High' | 'Medium' | 'Low';

export type MaintainableItem = {
  id: string;
  label: string;
  /** Cross-stream risk-register id (a20-risk owns the register itself).
   *  Undefined when the item has no register row — never invent one. */
  riskId?: string;
  criticality: CriticalityTier;
  taskType: FmeaTaskType;
  conditionSignal: string;
  frequency: string;
  shutdownWindowId?: string;
  /** Flowsheet subprocess for an item the downtime event log does not carry.
   *  The log's own `ITEM_SUBPROCESS` is the primary link (one fact, one place);
   *  this field exists only for the items missing from it, and the probe
   *  asserts every logged id leaves it undefined. */
  subprocessId?: string;
};

/** [syn] Asset labels in the seeded maintenance WO table that name the same
 *  maintained item as the register. Unmatched labels stay unmapped; their
 *  work orders do not get assigned to a merely similar asset. */
export const WORK_ORDER_ITEM_BY_ASSET_LABEL: Readonly<Record<string, DowntimeItemId>> = {
  'Ball Mill': 'BM01',
  'Primary Crusher': 'CR01',
  'Cyclone Feed Pump': 'GP01',
};

/** Seed-table labels with no one-to-one maintainable-register item. */
export const WORK_ORDER_UNMAPPED_ASSET_LABELS = [
  'Pb Recleaner',
  'Pb/Ag Filter',
  'Regrind Mill',
  'Tails Pump 4',
  'Zn Thickener',
  'Cyclone cluster',
] as const;

/** Risk ids and criticality tiers read from the `a20-risk` register
 *  (`modules/risk/seed.ts`, RISK_ENTRIES) — tier follows the register's own
 *  RPN band ≥140 Critical · 100–139 High · 70–99 Medium · <70 Low. Four of
 *  the ten items (CR01, Pb rougher bank, TK03, belt conveyors) have no
 *  register row: they carry no `riskId` and their criticality stays this
 *  stream's own [syn] read (the panel-level `[syn]` chip covers it). */
export const MAINTAINABLE_ITEMS: readonly MaintainableItem[] = [
  {
    id: 'BM01',
    label: 'BM01 Ball mill (trunnion bearing)',
    riskId: 'RK-02',
    criticality: 'Critical',
    taskType: 'CBM + PM',
    conditionSignal: 'Oil analysis (Fe/Cu, water) + bearing temp TE-622-625, continuous DCS',
    frequency: 'Monthly oil · continuous temperature',
    // No shutdown window (D16, polish.P21): the bearing's proposed 4 h stop is
    // the case's draft action, outside the schedule until it is approved.
  },
  {
    id: 'CR01',
    label: 'CR01 Primary crusher',
    criticality: 'High',
    taskType: 'PM + CBM',
    conditionSignal: 'Amperage trend + crusher gap (CSS) check',
    frequency: 'Monthly',
  },
  {
    id: 'AF01',
    label: 'AF01 Apron feeder',
    riskId: 'RK-01',
    criticality: 'High',
    taskType: 'Operational + PM',
    conditionSignal: 'Drive motor current + operator visual',
    frequency: 'Continuous / monthly PM',
  },
  {
    id: 'GP01',
    label: 'GP01 Cyclone feed pump',
    riskId: 'RK-04',
    criticality: 'Low',
    taskType: 'CBM + PM',
    conditionSignal: 'Vibration route + quarterly impeller measurement (VSD% vs discharge pressure)',
    frequency: 'Monthly / quarterly',
  },
  {
    id: 'PB-RGH',
    label: 'Pb rougher bank',
    criticality: 'Medium',
    taskType: 'CBM + Operational',
    conditionSignal: 'Air flow trend + froth camera operator observation',
    frequency: 'Continuous',
  },
  {
    id: 'ZN-RGH',
    label: 'Zn rougher bank',
    riskId: 'RK-05',
    criticality: 'Critical',
    taskType: 'Operational + PM',
    conditionSignal: 'pH trend + operator observation',
    frequency: 'Continuous / monthly PM',
  },
  {
    id: 'PB-THK',
    label: 'Pb/Ag thickener (flocculant)',
    riskId: 'RK-08',
    criticality: 'Medium',
    taskType: 'CBM + PM',
    conditionSignal: 'Flocculant dosing rate + underflow density trend',
    frequency: 'Continuous / quarterly',
    shutdownWindowId: 'sw-2',
    // [syn] — the downtime event log carries no PB-THK row; the Pb/Ag
    // thickener is concentrate thickening on the filter subprocess.
    subprocessId: 'sp-filter',
  },
  {
    id: 'TK03',
    label: 'TK03 Tails thickener',
    criticality: 'Medium',
    taskType: 'PM + CBM',
    conditionSignal: 'Rake torque + underflow density trend',
    frequency: 'Continuous / monthly inspection',
    shutdownWindowId: 'sw-3',
  },
  {
    id: 'GP40',
    label: 'GP40 Paste pump',
    riskId: 'RK-09',
    criticality: 'Medium',
    taskType: 'CBM',
    conditionSignal: 'Discharge pressure / VSD trend',
    frequency: 'Continuous DCS',
    shutdownWindowId: 'sw-3',
  },
  {
    id: 'CV-CRUSH',
    label: 'Belt conveyors CV-001–CV-010',
    criticality: 'High',
    taskType: 'CBM + Operational',
    conditionSignal: 'Drive motor current (CBM) + belt tracking operator check (Operational)',
    frequency: 'Continuous / daily walk-down',
    // [syn] — the downtime event log carries no CV-CRUSH row; the conveyor
    // train is ore receiving and crushing.
    subprocessId: 'sp-crush',
  },
];

// ---------------------------------------------------------------------------
// Short area vocabulary for the register's own `Area` column, keyed by
// subprocess id. A subprocess `name` is a flowsheet sentence
// (`Pb/Ag flotation (rougher → regrind → cleaner)`) and never belongs in a
// table cell; this is the one-word decode.
//
// The first four entries are the a20-risk register's own `circuit` strings
// verbatim — the risk route already names these subprocesses on the glass, and
// a second wording for the same area is a second fact. The last two are [syn]:
// no register row sits on `sp-pb`, so there is no string to inherit; RK-09
// sits on `sp-paste` under the register's `Thickeners` circuit, which names
// the thickener-adjacent sheet, not the paste and tails area, so this table
// overrides it.
//
// Total over the ids the ten item rows resolve to — there is no fallback, and
// the probe asserts a key exists for every id the rows carry.
// ---------------------------------------------------------------------------

export const AREA_LABEL: Record<string, string> = {
  'sp-crush': 'Crushing',
  'sp-grind': 'Ball Mill',
  'sp-zn': 'Zn Flotation',
  'sp-filter': 'Thickeners',
  'sp-paste': 'Paste & tails', // [syn] — overrides RK-09's `Thickeners` circuit string
  'sp-pb': 'Pb flotation', // [syn] — no register row sits on this subprocess
};

// ---------------------------------------------------------------------------
// The condition-monitoring inventory names assemblies (`BM01 trunnion
// bearing`, `Cyclone Feed Pump`), the register names items (`BM01`, `GP01`).
// This is the one place the two vocabularies are joined.
//
// `cm-7` is deliberately absent: a belt scale is instrumentation, not the
// conveyor item, so the belt conveyor row has no point and renders the muted
// dash — a measured absence, not a guessed link.
// ---------------------------------------------------------------------------

// [syn] — a join between two real inventories; neither names the other's id
export const CM_POINT_ITEM: Record<string, string> = {
  'cm-1': 'BM01',
  'cm-2': 'BM01',
  'cm-3': 'BM01',
  'cm-4': 'BM01',
  'cm-5': 'GP01',
  'cm-6': 'ZN-RGH',
};

/** The worst chip across an item's points, decoded for the column. */
export const CONDITION_LABEL: Record<string, string> = {
  warn: 'Alert',
  rising: 'Rising',
  stable: 'Stable',
};

export type ShutdownWindow = {
  id: string;
  area: string;
  driver: string;
  plannedStart: string;
  durationHours: number;
  crew: string;
};

/** [syn] — v19 M9 §6: "no shutdown calendar or duration table exists in the
 *  Functional Description or FMEA." Reformatted from the real PM Schedule
 *  rows already on the PM Schedule tab (`bunker-hill-seed.ts` `pm-3`,
 *  `pm-5`) rather than inventing new dates or durations. The Grinding window
 *  (`sw-1`, "BM01 trunnion bearing inspect/replace (WO-441)", 10 Jun · 12 h)
 *  left with the D16 seed fix (polish.P21): WO-441 is the 04 Jun trip, and the
 *  bearing's 4 h inspection stop on Sat 13 Jun is the case's draft action. */
export const SHUTDOWN_WINDOWS: readonly ShutdownWindow[] = [
  {
    id: 'sw-2',
    area: 'Pb circuit',
    driver: 'Pb/Ag thickener maintenance window',
    plannedStart: '13 Jun',
    durationHours: 8,
    crew: 'K. Jones',
  },
  {
    id: 'sw-3',
    area: 'Tailings',
    driver: 'Tailings thickening / paste circuit maintenance window',
    plannedStart: '16 Jun',
    durationHours: 5,
    crew: 'T. Brown',
  },
];
